import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

// SAP Logo URL
export const SAP_LOGO_URL = "https://customer-assets.emergentagent.com/job_4f1d3423-7965-4d73-abde-e7981c96054c/artifacts/xgkhceye_pngwing.com.png";

/**
 * SAP Compliance Components
 * Reusable blocks for Services à la Personne compliance throughout the site
 */

// Small SAP compliance badge (for footer, cards, etc.)
export const SAPBadge = ({ showLogo = true, size = 'default' }) => {
  const sizeClasses = {
    small: 'text-xs py-1 px-2',
    default: 'text-sm py-2 px-3',
    large: 'text-base py-3 px-4'
  };

  return (
    <div className="flex items-center gap-3">
      {showLogo && (
        <img 
          src={SAP_LOGO_URL}
          alt="Logo Services à la personne"
          className={size === 'small' ? 'h-8 w-auto' : size === 'large' ? 'h-14 w-auto' : 'h-10 w-auto'}
          loading="lazy"
        />
      )}
      <div>
        <p className={`font-semibold text-emerald-700 ${sizeClasses[size]}`}>
          Service à la personne
        </p>
        <p className="text-gray-600 text-xs">
          Éligible au crédit d'impôt de 50%
        </p>
      </div>
    </div>
  );
};

// SAP compliance mention (inline text)
export const SAPMention = ({ className = '' }) => (
  <p className={`text-sm text-emerald-700 font-medium ${className}`}>
    Service à la personne – éligible au crédit d'impôt de 50% (selon conditions fiscales).
  </p>
);

// Exclusions block - what's NOT covered by SAP
export const SAPExclusions = ({ variant = 'default', className = '' }) => {
  const exclusions = [
    "Plomberie",
    "Électricité",
    "Chauffage/gaz",
    "Gros travaux / bâtiment",
    "Élagage à risque",
    "Débarras / déménagement",
    "Traitements phytosanitaires interdits"
  ];

  if (variant === 'inline') {
    return (
      <p className={`text-xs text-gray-500 ${className}`}>
        <span className="font-medium text-gray-600">Hors périmètre SAP :</span>{' '}
        {exclusions.join(' • ')}
      </p>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start gap-2">
          <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={16} />
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Hors périmètre SAP</p>
            <p className="text-xs text-gray-500">{exclusions.join(' • ')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Default: full block with list
  return (
    <div className={`bg-amber-50 border border-amber-200 rounded-xl p-5 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="text-amber-600" size={20} />
        <h4 className="font-semibold text-amber-800">Hors périmètre SAP</h4>
      </div>
      <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {exclusions.map((item, i) => (
          <li key={i} className="flex items-center gap-1.5 text-sm text-amber-700">
            <XCircle size={14} className="text-amber-500 flex-shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Petit bricolage rule reminder
export const PetitBricolageRule = ({ className = '' }) => (
  <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
    <div className="flex items-start gap-2">
      <Info className="text-blue-500 flex-shrink-0 mt-0.5" size={18} />
      <div>
        <p className="text-sm font-medium text-blue-800">Petit bricolage (SAP)</p>
        <p className="text-sm text-blue-700">
          Tâches simples uniquement, durée maximale 2 heures.
        </p>
      </div>
    </div>
  </div>
);

// Avance immédiate disclaimer
export const AvanceImmediateDisclaimer = ({ className = '' }) => (
  <p className={`text-sm text-gray-600 ${className}`}>
    <strong>Avance immédiate (Urssaf) :</strong> option possible si éligible et après activation. Jamais automatique.
  </p>
);

// Price display with TTC and credit d'impôt
export const SAPPrice = ({ 
  price, 
  priceAfterCredit, 
  unit = '', 
  size = 'default',
  showCredit = true,
  className = '' 
}) => {
  const sizeClasses = {
    small: { price: 'text-lg', after: 'text-sm' },
    default: { price: 'text-2xl', after: 'text-base' },
    large: { price: 'text-3xl', after: 'text-lg' }
  };

  return (
    <div className={className}>
      <p className={`font-bold text-gray-900 ${sizeClasses[size].price}`}>
        {price} {unit && <span className="text-gray-500 font-normal text-base">{unit}</span>}
        <span className="text-sm font-normal text-gray-500 ml-1">TTC</span>
      </p>
      {showCredit && priceAfterCredit && (
        <p className={`text-emerald-600 ${sizeClasses[size].after}`}>
          ≈ {priceAfterCredit} {unit} après crédit d'impôt 50%
        </p>
      )}
    </div>
  );
};

// SAP services list - what IS covered
export const SAPServicesList = ({ className = '' }) => {
  const services = [
    { name: "Jardinage courant", desc: "Tonte, taille haies, débroussaillage" },
    { name: "Vigilance résidence", desc: "Surveillance de votre bien en votre absence" },
    { name: "Petit bricolage", desc: "Tâches simples, ≤ 2h" },
    { name: "Entretien maison léger", desc: "Nettoyage courant" }
  ];

  return (
    <div className={`bg-emerald-50 border border-emerald-200 rounded-xl p-5 ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <img 
          src={SAP_LOGO_URL}
          alt="Logo Services à la personne"
          className="h-10 w-auto"
          loading="lazy"
        />
        <h4 className="font-semibold text-emerald-800">Services à la personne</h4>
      </div>
      <ul className="space-y-2">
        {services.map((service, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium text-emerald-800">{service.name}</span>
              <span className="text-emerald-600 text-sm ml-1">— {service.desc}</span>
            </div>
          </li>
        ))}
      </ul>
      <p className="text-xs text-emerald-600 mt-4">
        Tous éligibles au crédit d'impôt de 50% (selon conditions fiscales)
      </p>
    </div>
  );
};

// Full SAP compliance block for key pages
export const SAPComplianceBlock = ({ showExclusions = true, showServices = false, className = '' }) => (
  <div className={`space-y-4 ${className}`}>
    <div className="bg-white border-2 border-emerald-300 rounded-2xl p-6">
      <div className="flex items-center gap-4 mb-4">
        <img 
          src={SAP_LOGO_URL}
          alt="Logo Services à la personne"
          className="h-14 w-auto"
          loading="lazy"
        />
        <div>
          <h3 className="text-xl font-bold text-emerald-800">Service à la personne</h3>
          <p className="text-emerald-600">Éligible au crédit d'impôt de 50%</p>
        </div>
      </div>
      <p className="text-gray-700 text-sm mb-4">
        Mes prestations d'entretien courant entrent dans le cadre des Services à la personne (SAP), vous permettant de bénéficier d'un crédit d'impôt de 50% sur les sommes dépensées, selon vos conditions fiscales personnelles.
      </p>
      <div className="flex flex-wrap gap-3 text-xs">
        <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">
          Déclaration SAP813136249
        </span>
        <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">
          Attestation fiscale fournie
        </span>
        <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">
          Paiement virement ou chèque
        </span>
      </div>
    </div>
    
    {showServices && <SAPServicesList />}
    {showExclusions && <SAPExclusions />}
  </div>
);

export default {
  SAPBadge,
  SAPMention,
  SAPExclusions,
  PetitBricolageRule,
  AvanceImmediateDisclaimer,
  SAPPrice,
  SAPServicesList,
  SAPComplianceBlock,
  SAP_LOGO_URL
};
